import { ACCEPTED_EXTENSIONS, extractTextFromFile, isAcceptedFileType } from '@/lib/extractFileText'
import { useRef, useState } from 'react'

const EXAMPLES = [
  'A class or group project',
  'A club, team or organization',
  'A part-time job or internship',
  'Volunteering or helping out',
]

interface IntakeIntroCardProps {
  starting: boolean
  onStart: (resumeText?: string) => void
}

export function IntakeIntroCard({ starting, onStart }: IntakeIntroCardProps) {
  const fileInput = useRef<HTMLInputElement>(null)
  const [resumeText, setResumeText] = useState<string | null>(null)
  const [resumeName, setResumeName] = useState<string | null>(null)
  const [resumeError, setResumeError] = useState<string | null>(null)
  const [reading, setReading] = useState(false)

  async function handleFile(file: File | undefined) {
    if (!file) return
    setResumeError(null)
    if (!isAcceptedFileType(file)) {
      setResumeError(`Please choose a ${ACCEPTED_EXTENSIONS.join(', ')} file.`)
      return
    }
    setReading(true)
    try {
      const text = (await extractTextFromFile(file)).trim()
      if (!text) {
        setResumeError("We couldn't find any text in that file.")
        return
      }
      setResumeText(text)
      setResumeName(file.name)
    } catch {
      setResumeError("We couldn't read that file. You can skip it and just start talking.")
    } finally {
      setReading(false)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  return (
    <section className="space-y-5 rounded-xl border border-border bg-background p-5">
      <div className="space-y-2">
        <h1 className="text-xl font-bold tracking-tight">Find the stories you already have</h1>
        <p className="text-sm text-muted-foreground">
          Think you have nothing to talk about? You almost certainly do — you just haven't been
          asked the right questions. Have a short chat and I'll help you turn real things you've
          done into interview-ready stories.
        </p>
      </div>

      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {EXAMPLES.map((e) => (
          <li key={e} className="rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground">
            {e}
          </li>
        ))}
      </ul>

      <div className="space-y-2 rounded-lg border border-dashed border-border p-3">
        <p className="text-xs font-medium">
          Optional: add your resume{' '}
          <span className="font-normal text-muted-foreground">
            so the questions can start from what's on it
          </span>
        </p>
        {resumeName ? (
          <div className="flex items-center justify-between gap-3 text-xs">
            <span className="truncate">{resumeName}</span>
            <button
              type="button"
              onClick={() => {
                setResumeText(null)
                setResumeName(null)
              }}
              className="shrink-0 text-muted-foreground underline underline-offset-2 hover:text-foreground"
            >
              Remove
            </button>
          </div>
        ) : (
          <>
            <input
              ref={fileInput}
              type="file"
              accept={ACCEPTED_EXTENSIONS.join(',')}
              onChange={(e) => void handleFile(e.target.files?.[0])}
              className="hidden"
              aria-label="Upload your resume"
            />
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={reading}
              className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-muted disabled:opacity-40"
            >
              {reading ? 'Reading…' : 'Choose a file'}
            </button>
          </>
        )}
        {resumeError && (
          <p role="alert" className="text-xs text-destructive">
            {resumeError}
          </p>
        )}
        <p className="text-[11px] text-muted-foreground">
          Used only to shape this conversation — we don't store it. It's processed by our AI
          provider like your answers are.
        </p>
      </div>

      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">About 10 minutes. You review every story.</p>
        <button
          type="button"
          onClick={() => onStart(resumeText ?? undefined)}
          disabled={starting || reading}
          className="rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-40"
        >
          {starting ? 'Starting…' : "Let's start"}
        </button>
      </div>
    </section>
  )
}
